import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, model, signal, viewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
    AutoComplete,
    type AutoCompleteCompleteEvent,
    type AutoCompleteLazyLoadEvent,
    AutoCompleteModule,
    type AutoCompleteSelectEvent,
} from '@openng/optimus-ui/autocomplete';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { TagModule } from '@openng/optimus-ui/tag';
import { injectParams } from 'ngxtension/inject-params';

import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    MODULE_ICON_MAP,
    type MODULES,
    OBJECT_DISPLAY_NAMES,
    OBJECT_INFO,
    OBJECT_MODULE_MAP,
    OBJECTS,
    type WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import { DateFromNowPipe } from '@app/shared/pipes/date-from-now.pipe';
import { ObjectIconPipe } from '@app/shared/pipes/object-color.pipe';
import { CommonAPIService } from '@app/shared/services/common-api.service';
import { RecentActivityService } from '@app/shared/services/recent-activity.service';
import { WindowService } from '@app/shared/services/window.service';
import { RouterHelper } from '@app/shared/utils/router-helper';

export interface GlobalSearchItem {
    id: number;
    name: string;
    createdByVisibleName: string;
    createdBy: string;
    createdDate: string;
    currentStatus: WORKFLOW_STATUS;
    objectType: OBJECTS;
    module: MODULES;
    version: number;
    projectSlug?: string;
}

@Component({
    selector: 'cor-global-search',
    imports: [
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        AutoCompleteModule,
        DialogModule,
        StatusIconComponent,
        SkeletonModule,
        DateFromNowPipe,
        ObjectIconPipe,
        NoDataComponent,
        BadgeModule,
        ButtonModule,
        TagModule,
        NgClass,
        NgStyle,
    ],
    templateUrl: './global-search.component.html',
    styleUrl: './global-search.component.scss',
    host: {
        '(window:keydown)': 'handleKeyboardEvent($event)',
    },
})
export class GlobalSearchComponent {
    readonly isSearchPanelVisible = model(false);
    readonly currentProject = injectParams<string>('projectSlug');

    readonly isLoading = signal(false);

    searchText = new FormControl('');

    // Pagination and Suggestions
    readonly suggestions = signal<GlobalSearchItem[]>([]);

    pageInfo = {
        pageSize: 20,
        skip: signal(0),
        totalItems: signal(0),
    };

    private readonly autoComplete = viewChild(AutoComplete);

    private readonly commonAPIService = inject(CommonAPIService);
    private readonly recentActivities = inject(RecentActivityService);
    private readonly windowService = inject(WindowService);
    private readonly router = inject(Router);

    protected readonly OBJECTS = OBJECTS;
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;
    protected readonly OBJECT_MODULE_MAP = OBJECT_MODULE_MAP;
    protected readonly MODULE_ICON_MAP = MODULE_ICON_MAP;
    protected readonly OBJECT_INFO = OBJECT_INFO;

    handleKeyboardEvent(event: KeyboardEvent) {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            this.openSearchPanel();
        }
    }

    protected setupSearchTextListener(event: AutoCompleteCompleteEvent) {
        // Reset pagination for new searches
        this.pageInfo.skip.set(0);
        this.onAutoCompleteFocus();
        this.fetchSuggestions(event.query, this.pageInfo.skip(), true);
    }

    private fetchSuggestions(query: string, skip: number, isNewSearch: boolean) {
        const searchText = query.trim();
        if (searchText.length === 0) return;

        this.isLoading.set(true);
        const params = {
            keyword: searchText,
            skip,
            take: this.pageInfo.pageSize,
        };

        this.commonAPIService
            .getGlobalSearch<GlobalSearchItem>(params)
            .subscribe({
                next: response => {
                    const newItems = response.result;
                    this.pageInfo.totalItems.set(response.pageInfo.total);
                    // Appends to the previous suggestion
                    this.suggestions.update(items =>
                        isNewSearch ? newItems : [...items, ...newItems],
                    );
                },
                error: (error: Error) => {
                    console.error('Error fetching suggestions:', error);
                },
            })
            .add(() => this.isLoading.set(false));
    }

    private resetSearch() {
        this.searchText.setValue('');
        this.isSearchPanelVisible.set(false);
        this.autoComplete().hide();
        this.suggestions.set([]);
        this.pageInfo.skip.set(0);
        this.pageInfo.totalItems.set(0);
    }

    protected onSearchScroll(event: AutoCompleteLazyLoadEvent) {
        if (
            event.last > this.pageInfo.skip() &&
            this.suggestions().length < this.pageInfo.totalItems()
        ) {
            this.pageInfo.skip.set(event.last);
            this.fetchSuggestions(this.searchText.value, this.pageInfo.skip(), false);
        }
    }

    protected onAutoCompleteFocus() {
        if (this.searchText.value.length === 0) {
            this.suggestions.set(
                this.recentActivities.getRecentActivities().map(({ objectType, details }) => ({
                    ...details,
                    objectType,
                })),
            );

            setTimeout(() => {
                if (this.suggestions().length > 0 && this.isSearchPanelVisible()) {
                    this.autoComplete().show();
                }
            }, 100);
        }
    }

    onSearchDialogHide() {
        this.resetSearch();
        this.autoComplete().ngOnDestroy();
    }

    protected openSearchPanel() {
        this.isSearchPanelVisible.set(true);
    }

    protected navigateToSelectedItem(item: AutoCompleteSelectEvent) {
        this.resetSearch();
        const link = RouterHelper.buildRoute({
            object: item.value.objectType,
            id: item.value.id,
            projectSlug: item.value.projectSlug,
        });

        if ((item.originalEvent as MouseEvent).ctrlKey) {
            this.windowService.openInNewTab(link);
        } else {
            this.router.navigateByUrl(link);
        }
    }

    protected openCreatePage(
        object:
            | OBJECTS.DATA_TABLE
            | OBJECTS.FOUNDATION_MODEL
            | OBJECTS.PIPELINE
            | OBJECTS.ANNOTATION_QUEUE,
    ) {
        this.resetSearch();
        this.router.navigateByUrl(
            RouterHelper.buildRoute({ object, id: null, projectSlug: this.currentProject() }),
        );
    }

    protected onClear() {
        this.searchText.setValue('');
        this.onAutoCompleteFocus();
    }
}
